import React, { useState } from 'react';
import { TrendingUp, Calendar, Info, BookOpen, Sparkles, Award } from 'lucide-react';

export interface TrendItem {
  date: string;
  count: number;
}

interface BookRegistrationTrendCardProps {
  trends?: TrendItem[];
}

export const BookRegistrationTrendCard: React.FC<BookRegistrationTrendCardProps> = ({ trends = [] }) => {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  // Generate 30 empty/fallback dates if no trends provided
  const safeTrends = trends && trends.length > 0 ? trends : Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    return {
      date: d.toISOString().split('T')[0],
      count: 0
    };
  });

  const maxVal = Math.max(...safeTrends.map((t) => t.count), 1);
  const totalRegistrations = safeTrends.reduce((acc, curr) => acc + curr.count, 0);
  const avgRegistrations = (totalRegistrations / safeTrends.length).toFixed(1);

  // Find peak day
  const peakItem = safeTrends.reduce((prev, current) => (prev.count > current.count ? prev : current), safeTrends[0]);

  // Helper to format date
  const formatDateLabel = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      
      const monthsIndo = [
        'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
        'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'
      ];
      return `${d.getDate()} ${monthsIndo[d.getMonth()]}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-1">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-100">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-none">
                Tren Pendaftaran Buku Baru
              </h3>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-indigo-50 text-indigo-700 border border-indigo-200 tracking-wider uppercase">
                30 Hari Terakhir
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Statistik harian pendaftaran judul naskah berstandar UIBN
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {peakItem && peakItem.count > 0 && (
            <div className="px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px] font-bold flex items-center gap-1.5 shadow-2xs">
              <TrendingUp className="w-3.5 h-3.5 text-indigo-600" />
              <span>Tertinggi: <strong>{peakItem.count} Buku</strong> ({formatDateLabel(peakItem.date)})</span>
            </div>
          )}
        </div>
      </div>

      {/* Sparkline Visual Grid */}
      <div className="relative pt-8 pb-3 bg-slate-50/50 rounded-2xl p-4 border border-slate-100/70">
        <div className="absolute top-3 left-4 flex items-center gap-1 text-[11px] font-bold text-indigo-600">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Interaktif: Arahkan kursor ke grafik kolom untuk melihat rincian tanggal</span>
        </div>

        {/* Dense columns container */}
        <div className="h-28 sm:h-32 flex items-end justify-between gap-[2px] sm:gap-[4px] relative">
          {safeTrends.map((t, idx) => {
            const heightPercent = maxVal > 0 ? Math.round((t.count / maxVal) * 100) : 0;
            const isHovered = activeIdx === idx;
            const isPeak = t.count === peakItem.count && peakItem.count > 0;

            return (
              <div
                key={`reg-trend-${t.date}-${idx}`}
                className="flex-1 h-full flex flex-col justify-end group cursor-pointer relative"
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseLeave={() => setActiveIdx(null)}
              >
                {/* Micro Floating Tooltip on Hover */}
                {isHovered && (
                  <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-30 bg-slate-900 text-white text-[10px] sm:text-[11px] rounded-xl py-1.5 px-2.5 shadow-xl border border-slate-800 whitespace-nowrap flex flex-col items-center pointer-events-none animate-in fade-in zoom-in-95 duration-100">
                    <span className="font-bold">{formatDateLabel(t.date)}</span>
                    <span className="text-indigo-300 font-extrabold mt-0.5">{t.count} Buku Terdaftar</span>
                    <div className="w-2 h-2 bg-slate-900 rotate-45 -mb-1 mt-0.5" />
                  </div>
                )}

                {/* Micro Bar */}
                <div 
                  className={`w-full rounded-t-sm sm:rounded-t-md transition-all duration-300 ${
                    isHovered
                      ? 'bg-indigo-600 scale-x-110 shadow-sm'
                      : isPeak
                      ? 'bg-gradient-to-t from-indigo-500 to-indigo-400 font-black'
                      : t.count > 0
                      ? 'bg-indigo-400'
                      : 'bg-slate-200 hover:bg-slate-300'
                  }`}
                  style={{ height: `${Math.max(heightPercent, t.count > 0 ? 15 : 6)}%` }}
                />
              </div>
            );
          })}
        </div>

        {/* Simple X-Axis Labels (Start, Middle, End) */}
        <div className="flex justify-between items-center text-[10px] text-slate-400 font-mono mt-3 px-1 border-t border-slate-100 pt-2.5">
          <span>{formatDateLabel(safeTrends[0].date)}</span>
          <span className="text-[9px] font-sans">Visualisasi 30 Hari</span>
          <span>{formatDateLabel(safeTrends[safeTrends.length - 1].date)}</span>
        </div>
      </div>

      {/* Footer statistics breakdown */}
      <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-indigo-500 shrink-0" />
          <span>
            Total pendaftaran baru: <strong className="text-slate-900 font-extrabold">{totalRegistrations} Buku</strong>
          </span>
          <span className="text-slate-300">•</span>
          <span>
            Rata-rata harian: <strong className="text-slate-900 font-extrabold">{avgRegistrations} Buku/hari</strong>
          </span>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-xs bg-indigo-500"></span>
            <span>Registrasi Aktif</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-xs bg-slate-200"></span>
            <span>Kosong</span>
          </span>
        </div>
      </div>
    </div>
  );
};
